/**
 * Cloud saves (docs/deploy.md): the game's saves mirrored to the server it was loaded from,
 * under a save code - no accounts, no personal data. This file is the public face and the
 * browser wiring; the parts are in the files beside it and run in node under test.
 */

import { createCloudSave } from './cloudSave';
import type { CloudSave, KeyValueStore } from './types';

export { createCloudSave, PUSH_DELAY_MS, RETRY_MAX_MS, RETRY_MIN_MS, SUSPEND_PUSH_DELAY_MS } from './cloudSave';
export { CODE_ALPHABET, CODE_LENGTH, formatCode, isValidCode, newCode, normaliseCode } from './saveCode';
export { createSyncedStorage } from './syncedStorage';
export { BACKUP_PREFIX, CLOUD_KEY, CLOUD_KEYS, STATE_KEY } from './types';
export type { CloudDeps, CloudSave, CloudStateName, CloudStatus, KeyValueStore } from './types';

function browserStore(): KeyValueStore | null {
  try {
    return window.localStorage;
  } catch {
    // Storage blocked: the game plays without remembering, and there is nothing to mirror.
    return null;
  }
}

/**
 * The cloud layer for this page, probed and pulled: when it resolves, localStorage holds the
 * newest save there is and the game may read it. It always resolves - where there is no save
 * server (the dev server, a static host, offline) in the time one small request takes to fail,
 * with a layer that is off and does nothing.
 */
export async function startCloudSave(): Promise<CloudSave> {
  const cloud = createCloudSave({
    storage: browserStore(),
    // Bound late so the call is `window.fetch(...)`: fetch refuses to run detached from its window.
    fetch: (url, init) => window.fetch(url, init),
    location: window.location,
  });
  try {
    await cloud.pullAtBoot();
  } catch {
    // Nothing in there is meant to throw; if it does, the game must still start.
  }
  return cloud;
}
